/**
 * awe-material-swatches.js
 *
 * Standalone custom element for Material swatch circles on product cards.
 * Does NOT use swatches-variant-picker-component to avoid Horizon's morphdom
 * replacing our circles on every variant fetch.
 *
 * On swatch click:
 *  1. Updates the product card image via productCard.updateProductImage()
 *  2. Updates the product card price via productCard.updatePrice()
 *  3. Updates the product card link href to include the variant
 *  4. Previews the variant image in the slideshow (if present)
 */

if (customElements.get('awe-material-swatches-picker')) { /* already loaded — skip re-declaration */ } else {

class AweMaterialSwatchesPicker extends HTMLElement {
  /** @type {AbortController|null} */
  #eventsController = null;

  /** @type {AbortController|null} */
  #fetchController = null;

  connectedCallback() {
    this.#eventsController = new AbortController();

    // Use event delegation on the element itself rather than querying child inputs.
    // This avoids a timing issue: the browser upgrades custom elements at the
    // opening tag (before children are parsed), so querySelectorAll would return
    // empty for every card after the first one.
    this.addEventListener('change', this.#onChange, {
      signal: this.#eventsController.signal,
    });
  }

  disconnectedCallback() {
    this.#eventsController?.abort();
    this.#fetchController?.abort();
    this.#eventsController = null;
    this.#fetchController = null;
  }

  #onChange = async (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const input = event.target;
    const variantId = input.dataset.firstAvailableOrFirstVariantId;
    const mediaId = input.dataset.optionMediaId;

    if (!variantId) return;

    const productCard = /** @type {any} */ (this.closest('product-card'));

    // ── 1. Immediate image preview via Horizon's public API ──────────────────
    if (mediaId && productCard && typeof productCard.previewVariant === 'function') {
      productCard.previewVariant(mediaId);
    }

    // ── 2. Fetch the updated section for price / availability / variant URL ──
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const productUrl = this.dataset.productUrl?.split('?')[0];
    if (!productUrl) return;

    const requestUrl = `${productUrl}?variant=${variantId}&section_id=section-rendering-product-card`;

    try {
      const response = await fetch(requestUrl, { signal: this.#fetchController.signal });
      if (!response.ok) return;

      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      // ── Price update ────────────────────────────────────────────────────────
      if (productCard && typeof productCard.updatePrice === 'function') {
        productCard.updatePrice({ detail: { data: { html } } });

        // morph overwrites <percentage-off> innerHTML back to `&nbsp;` and
        // connectedCallback doesn't re-fire. Re-render after morph completes.
        productCard.querySelectorAll('percentage-off').forEach((el) => {
          const value = parseFloat(el.dataset.number);
          if (isNaN(value)) return;
          const rounded = Math.floor(value / 5) * 5;
          el.innerHTML = `${rounded}% off`;
        });
      }

      // ── Main image update (uses variant's featured_media) ───────────────────
      const textContent = html.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (textContent && productCard && typeof productCard.updateProductImage === 'function') {
        try {
          const variantData = JSON.parse(textContent);
          productCard.updateProductImage({ detail: { resource: variantData, data: { html } } });
        } catch {
          // JSON parse failure — skip image update
        }
      }

      // ── Product card link → include variant param ───────────────────────────
      const productCardLink = productCard?.querySelector('[ref="productCardLink"]');
      if (productCardLink instanceof HTMLAnchorElement) {
        const href = new URL(productCardLink.href, window.location.origin);
        href.searchParams.set('variant', variantId);
        productCardLink.href = href.toString();
      }

      // ── Add-to-cart availability ────────────────────────────────────────────
      // Use our own input's availability rather than the response's (which
      // doesn't include our Material swatches and could show wrong state).
      const isAvailable = input.dataset.optionAvailable === 'true';
      const addToCartBtn = productCard?.querySelector('.add-to-cart__button button');
      if (addToCartBtn instanceof HTMLButtonElement) {
        addToCartBtn.disabled = !isAvailable;
      }

      // ── Sync selected variant into the product form ─────────────────────────
      // Scoped to this productCard so other cards on the page are not affected.
      const variantIdInput = productCard?.querySelector('input[name="id"]');
      if (variantIdInput instanceof HTMLInputElement) {
        variantIdInput.value = variantId;
        variantIdInput.disabled = !isAvailable;
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('[awe-material-swatches]', err);
      }
    }
  };
}

  customElements.define('awe-material-swatches-picker', AweMaterialSwatchesPicker);
} // end else — guard against double-load
